import { lastValueFrom, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ResponseConnectionStatus, LabelNamePair, IntegrationSettingType, IntegrationStatus, IntegrationState, LeadInformation, MaList, MaField, CrmCompaniesResponse, IntegrationConnConfig, IntegrationFeaturesConfig, IntegrationConfig } from '../../models/integrations';
import { environment as config } from '../../../../../environments/environment';
import { integrations } from './integrations';
import { TokenInterceptorSkipAccountHeader } from 'src/app/modules/shared/constants/interceptors-headers';
import { IntegrationCrmItem, IntegrationCrmItemsRequest, IntegrationCrmTotalItemsRequest, IntegrationType } from 'src/app/modules/shared/models/integration.models';
import { AccountContextService } from 'src/app/modules/shared/services/account-context.service';
import { Utils } from 'src/app/modules/shared/classes/utils';
import { RulesConfig } from 'src/app/modules/shared/models/rules.models';

@Injectable({
  providedIn: 'root'
})
export class IntegrationsService {
  private http = inject(HttpClient);
  private accountContext = inject(AccountContextService);

  isCrm(integrationType: IntegrationType): boolean {
    return integrationType === IntegrationType.salesforce;
  }

  isCrmSupported(activeIntegrations: IntegrationStatus[]): boolean {
    return integrations.filter(i => i.supportDynamicTargetList && activeIntegrations.map(x => x.integrationId).indexOf(i.id) > -1).length > 0;
  }

  getActiveIntegrations(accountId?: number): Observable<IntegrationStatus[]> {
    const headers = accountId
      ? new HttpHeaders().set('AccountId', accountId.toString()).set(TokenInterceptorSkipAccountHeader, '')
      : undefined;
    return this.http.get<IntegrationStatus[]>(`${config.adminBaseUrl}/api/integrations/active`, { headers });
  }

  getConfiguredIntegrations(): Promise<IntegrationConfig[]> {
    return lastValueFrom(this.http.get<IntegrationConfig[]>(`${config.adminBaseUrl}/api/integrations/configured`));
  }

  async getConnectionConfig<T extends IntegrationSettingType>(integrationId: number): Promise<IntegrationConnConfig<T>> {
    return lastValueFrom(this.http.get<IntegrationConnConfig<T>>(`${config.adminBaseUrl}/api/integrations/${integrationId}/conn-config`));
  }

  async getFeaturesConfig(integrationId: number): Promise<IntegrationFeaturesConfig> {
    return lastValueFrom(
      this.http.get<IntegrationFeaturesConfig>(`${config.adminBaseUrl}/api/integrations/${integrationId}/features-config`)
    );
  }

  changeStatus(integration: number, stat: IntegrationState): Observable<unknown> {
    return this.http.put(`${config.adminBaseUrl}/api/integrations/${integration}/change_status`,
      {
        status: stat
      }
    );
  }
  async delete(integrationId: number): Promise<void> {
    await lastValueFrom(
      this.http.delete(`${config.adminBaseUrl}/api/integrations/${integrationId}`, { responseType: 'text' }));
  }

  checkConnection<T extends IntegrationSettingType>(integrationId: number, params: T): Observable<ResponseConnectionStatus> {
    return this.http.post<ResponseConnectionStatus>(`${config.adminBaseUrl}/api/integrations/${integrationId}/check-connection`, params);
  }

  saveIntegrationSettings<T extends IntegrationSettingType>(integrationId: number, params: T): Observable<void> {
    return this.http.post<void>(`${config.adminBaseUrl}/api/integrations/${integrationId}/save`, params);
  }

  async saveFeaturesConfig(integrationId: number, featuresConfig: IntegrationFeaturesConfig): Promise<void> {
    return lastValueFrom(
      this.http.put<void>(`${config.adminBaseUrl}/api/integrations/${integrationId}/features-config`, featuresConfig)
    );
  }

  getIntegrationSalesorceFilelds(): Observable<{ data: LabelNamePair[] }> {
    return this.http.get<{ data: LabelNamePair[] }>(`${config.adminBaseUrl}/api/integrations/loadField`);
  }

  getIntegrationsFields(integration: IntegrationType): Observable<MaField[]> {
    return this.http.get<MaField[]>(`${config.adminBaseUrl}/api/integrations/${integration}/fields`);
  }

  getIntegrationsLists(integration: IntegrationType): Observable<MaList[]> {
    return this.http.get<MaList[]>(`${config.adminBaseUrl}/api/integrations/${integration}/lists`);
  }

  getLeadByEmail(email: string, integrationId: number, fields: string[]): Observable<LeadInformation> {
    const params = {
      email,
      provider: integrationId.toString(),
      SpecificFields: fields
    };
    return this.http.get<LeadInformation>(`${config.adminBaseUrl}/api/integrations/leadByEmail`, { params });
  }
  getMatchingGoals(email): Observable<number[]> {
    return this.http.get<number[]>(`${config.adminBaseUrl}/api/integrations/matchingGoals?email=${email}`);
  }
  getCrmCompaniesFields(integration: IntegrationType, crmObject: string): Observable<MaField[]> {
    return this.http.get<MaField[]>(`${config.adminBaseUrl}/api/integrations/${integration}/companies/crmfields/${crmObject}`);
  }

  getCrmCompanies(integration: IntegrationType, listConfig: RulesConfig, configObject: string, targetListId: number, limit: number): Observable<CrmCompaniesResponse> {
    return this.http.post<CrmCompaniesResponse>(`${config.adminBaseUrl}/api/integrations/${integration}/companies`,
      { targetListId, listConfig, configObject, provider: integration, limit }
    );
  }

  getCrmItemsFields(integration: IntegrationType, crmObject: string, accountId?: number): Observable<MaField[]> {
    const urlParams = accountId ? new HttpParams().set('accountid', accountId.toString()) : undefined;
    return this.http.get<MaField[]>(`${config.adminBaseUrl}/api/integrations/${integration}/crm_items/fields?objectName=${crmObject}`, { params: urlParams });
  }

  getCrmItems(integration: IntegrationType, request: IntegrationCrmItemsRequest): Observable<IntegrationCrmItem[]> {
    return this.http.post<IntegrationCrmItem[]>(`${config.adminBaseUrl}/api/integrations/${integration}/crm_items/items`, request);
  }

  getCrmTotalItems(integration: IntegrationType, request: IntegrationCrmTotalItemsRequest): Observable<number> {
    return this.http.post<number>(`${config.adminBaseUrl}/api/integrations/${integration}/crm_items/total`, request);
  }
  isSupportDeals(integrationsConfig: IntegrationConfig[], supportsMaDeals: boolean): boolean {
    if (!this.accountContext.supports('showDeals')) return false;
    if (Utils.isDemo()) return true;
    const isActiveSF = !!integrationsConfig.find(i => i.integrationId === IntegrationType.salesforce && i.isActive);
    const isActiveHS = !!integrationsConfig.find(i => i.integrationId === IntegrationType.hubspot && i.isActive);
    return isActiveSF || (isActiveHS && supportsMaDeals);
  }
}
